import React, { useRef } from 'react'
import dynamic from 'next/dynamic'

import { ProductPageData } from '@thg-commerce/enterprise-aurora-product/resolvers/ProductPage/types'
import { WishlistButtonType } from '@thg-commerce/enterprise-components/Product/src/WishlistButton/WishlistButton'
import { i18n } from '@thg-commerce/enterprise-core'

import { useProductPage } from '../../context'
import { WishlistButtonRendererProps } from '../../renderers/WishlistButtonRenderer'

import {
  BagIcon,
  ButtonWrapper,
  IconographyContainer,
  IconographyWrapper,
  VideoIcon,
  WishWrapper,
} from './styles'

const WishlistButtonRenderer = dynamic<WishlistButtonRendererProps>(() =>
  import('../../renderers/WishlistButtonRenderer').then(
    (mod) => mod.WishlistButtonRenderer,
  ),
)

interface PDPIconographyProps {
  product: ProductPageData
  hasHulla: boolean
  jumpToVideo: (num: number) => void
  disableWishlist?: boolean
}

const scrollToHulla = (e: React.MouseEvent) => {
  e.preventDefault()

  const targetElement = document.getElementById('hulla')
  if (targetElement) {
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

export const PDPIconography: React.FC<PDPIconographyProps> = (props) => {
  const { selectedVariant } = useProductPage()
  const wishRef = useRef<HTMLDivElement>(null)

  // Check if the selected variant has a video
  const selectedVariantHasVideo =
    selectedVariant.gallery?.images.findIndex((image) => image.isVideoUrl) || -1

  // Check if any variant in the product has a video
  const anyVariantHasVideo =
    props.product.variants?.some((variant) =>
      variant.gallery?.images?.some((image) => image.isVideoUrl),
    ) ||
    !!props.product.video ||
    false

  // Use selected variant's video index for jumping to the correct video
  const videoIndex = selectedVariantHasVideo

  const i18nText = {
    wishlist: i18n('product.pdpiconography.wishlist'),
    hullabalook: i18n('product.pdpiconography.hullabalook'),
    video: i18n('product.pdpiconography.video'),
  }

  const handleWishlistClick = () => {
    const button = wishRef.current?.querySelector('button')
    if (button) button.click()
  }
  const hoverButton = () => {
    const button = wishRef.current?.querySelector('button')
    if (button) button.focus()
  }
  const unHoverButton = () => {
    const button = wishRef.current?.querySelector('button')
    if (button) button.blur()
  }

  const handleVideo = () => {
    props.jumpToVideo(videoIndex)
  }

  const children = [
    !props.disableWishlist,
    props.hasHulla,
    anyVariantHasVideo,
  ].filter(Boolean).length

  return (
    <IconographyWrapper>
      <IconographyContainer childCount={children}>
        {!props.disableWishlist && (
          <WishWrapper
            data-testid="wishlist-wrapper"
            ref={wishRef}
            onClick={handleWishlistClick}
            onMouseEnter={hoverButton}
            onMouseLeave={unHoverButton}
          >
            <WishlistButtonRenderer
              selectedVariant={selectedVariant || props.product.defaultVariant}
              buttonType={WishlistButtonType.SMALL}
              itemCategories={props.product.content}
              brandName={props.product.brand?.name}
              externalIdentifier={props.product.externalIdentifier}
            />
            <span>{i18nText.wishlist}</span>
          </WishWrapper>
        )}
        {props.hasHulla && (
          <ButtonWrapper
            data-testid="hullabalook-wrapper"
            renderedAs="a"
            href="#hulla"
            emphasis="low"
            onClick={scrollToHulla}
          >
            <BagIcon />
            {i18nText.hullabalook}
          </ButtonWrapper>
        )}
        {anyVariantHasVideo && (
          <ButtonWrapper
            data-testid="video-wrapper"
            onClick={handleVideo}
            emphasis="low"
          >
            <VideoIcon />
            {i18nText.video}
          </ButtonWrapper>
        )}
      </IconographyContainer>
    </IconographyWrapper>
  )
}
